// src/pages/Withdraw/PlanWithdrawPage.jsx - COMPLETE FIX
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { planService } from '../../services/planService';
import { useCurrency } from '../../context/CurrencyContext';
import { API_BASE_URL } from '../../utils/constants';
import './PlanWithdrawPage.css';

const PlanWithdrawPage = () => {
  const { planId } = useParams();
  const navigate = useNavigate();
  const { formatCurrency, currency } = useCurrency();
  
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [withdrawing, setWithdrawing] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [exchangeRate, setExchangeRate] = useState(null);

  useEffect(() => {
    fetchPlanDetails();
    fetchExchangeRate();
  }, [planId, currency]);

  const fetchExchangeRate = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/currency/rate/`, {
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });
      

      if (response.ok) {
        const data = await response.json();
        setExchangeRate(data);
        console.log('💱 Exchange rate fetched:', data);
      }
    } catch (err) {
      console.error('Error fetching exchange rate:', err);
    }
  };

  const fetchPlanDetails = async () => {
    try {
      const planData = await planService.getPlanDetail(planId);
      console.log('📦 Plan data fetched:', planData);
      setPlan(planData);
    } catch (err) {
      setError('Failed to load plan details');
    } finally {
      setLoading(false);
    }
  };

  const handleWithdraw = async () => {
    if (!plan || !plan.is_matured) return;

    setWithdrawing(true);
    setError(null);
    setSuccess(null);

    try {
      console.log('=== WITHDRAWAL DEBUG START ===');
      console.log('💰 Plan object:', plan);
      console.log('📊 Plan data:', {
        id: plan.id,
        current_balance: plan.current_balance,
        current_balance_ngn: plan.current_balance_ngn,
        display_currency: plan.display_currency,
        user_currency: currency,
        exchangeRate: exchangeRate
      });
      
      // ✅ CRITICAL FIX: Use the raw NGN balance from backend, not converted amount
      let amountToWithdrawNGN;
      
      if (plan.current_balance_ngn) {
        // Use the actual NGN balance from the database
        amountToWithdrawNGN = parseFloat(plan.current_balance_ngn);
        console.log('✅ Using raw NGN balance from backend:', amountToWithdrawNGN);
      } else {
        // Fallback: Try to convert (but this will be inaccurate)
        console.warn('⚠️ No NGN balance provided, falling back to conversion');
        const displayBalance = parseFloat(plan.current_balance);
        
        if (plan.display_currency === 'USD' && exchangeRate) {
          const usdToNgn = parseFloat(exchangeRate.usd_to_ngn);
          amountToWithdrawNGN = displayBalance * usdToNgn;
          console.log('💱 Fallback conversion:', {
            displayBalanceUSD: displayBalance,
            usdToNgn: usdToNgn,
            resultNGN: amountToWithdrawNGN
          });
        } else {
          amountToWithdrawNGN = displayBalance;
          console.log('💵 Using display balance as NGN:', amountToWithdrawNGN);
        }
      }
      
      // Round to 2 decimal places
      amountToWithdrawNGN = Math.round(amountToWithdrawNGN * 100) / 100;
      console.log('📤 FINAL AMOUNT TO SEND (NGN):', amountToWithdrawNGN);
      
      // Make the withdrawal request
      console.log('🚀 Calling withdrawFromPlan with:', {
        planId: plan.id,
        amount: amountToWithdrawNGN
      });
      
      const result = await planService.withdrawFromPlan(plan.id, amountToWithdrawNGN);
      
      console.log('✅ Withdrawal result:', result);
      console.log('=== WITHDRAWAL DEBUG END ===');
      
      if (result.message) {
        setSuccess(result.message);
        
        // Trigger dashboard refresh
        const event = new CustomEvent('planStatusChanged');
        window.dispatchEvent(event);
        
        // Navigate after short delay
        setTimeout(() => {
          navigate('/dashboard');
        }, 2000);
      }
    } catch (err) {
      console.error('❌ Withdrawal error:', err);
      setError(err.message || 'Failed to withdraw funds');
    } finally {
      setWithdrawing(false);
    }
  };

  if (loading) {
    return (
      <div className="plan-withdraw-container">
        <div className="plan-withdraw-loading">Loading plan details...</div>
      </div>
    );
  }

  if (!plan) {
    return (
      <div className="plan-withdraw-container">
        <div className="plan-withdraw-error">Plan not found</div>
        <button className="back-button" onClick={() => navigate('/dashboard')}>
          Back to Dashboard
        </button>
      </div>
    );
  }

  const isMatured = plan.is_matured;
  const displayBalance = parseFloat(plan.current_balance);

  return (
    <div className="plan-withdraw-container">
      <div className="plan-withdraw-header">
        <button className="back-button" onClick={() => navigate('/dashboard')}>
          ← Back to Dashboard
        </button>
        <h1>Withdraw from Investment Plan</h1>
        <p>Withdraw funds from your matured investment plan</p>
      </div>

      <div className="plan-withdraw-content">
        {/* Plan Details Card */}
        <div className="plan-details-card">
          <h2>{plan.plan_name}</h2>
          
          <div className="plan-details-grid">
            <div className="detail-item">
              <span className="label">Initial Investment:</span>
              <span className="value">{formatCurrency(parseFloat(plan.initial_investment_amount))}</span>
            </div>
            
            <div className="detail-item">
              <span className="label">Current Balance:</span>
              <span className="value highlight">{formatCurrency(displayBalance)}</span>
            </div>
            
            <div className="detail-item">
              <span className="label">Weekly Growth Earned:</span>
              <span className="value">{formatCurrency(parseFloat(plan.total_weekly_growth || 0))}</span>
            </div>
            
            <div className="detail-item">
              <span className="label">Plan Status:</span>
              <span className={`status ${isMatured ? 'matured' : 'locked'}`}>
                {isMatured ? 'Matured' : 'Locked'}
              </span>
            </div>
            
            <div className="detail-item">
              <span className="label">Maturity Date:</span>
              <span className="value">
                {new Date(plan.lock_end_date).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </span>
            </div>
          </div>
        </div>

        {/* Withdrawal Section */}
        <div className="withdrawal-section">
          <h3>Withdrawal</h3>
          
          {!isMatured ? (
            <div className="locked-message">
              <div className="lock-icon">🔒</div>
              <h4>Plan Still Locked</h4>
              <p>
                Your plan is still locked and will mature on{' '}
                {new Date(plan.lock_end_date).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}. 
                You can withdraw your funds after the lock period ends.
              </p>
              <button 
                className="back-to-dashboard-btn"
                onClick={() => navigate('/dashboard')}
              >
                Back to Dashboard
              </button>
            </div>
          ) : (
            <div className="withdrawal-available">
              <div className="available-amount">
                <span className="label">Available for withdrawal:</span>
                <span className="amount">{formatCurrency(displayBalance)}</span>
              </div>
              
              <div className="withdrawal-info">
                <p>✅ Full withdrawal of {formatCurrency(displayBalance)} will be processed</p>
                <p>💰 Funds will be transferred to your wallet immediately</p>
                {plan.display_currency === 'USD' && exchangeRate && (
                  <p className="conversion-note">
                    💱 Amount will be converted to NGN at current rate (1 USD = ₦{parseFloat(exchangeRate.usd_to_ngn).toFixed(2)})
                  </p>
                )}
              </div>

              {error && <div className="error-message">{error}</div>}
              {success && <div className="success-message">{success}</div>}

              <button
                className={`withdraw-button ${withdrawing ? 'loading' : ''}`}
                onClick={handleWithdraw}
                disabled={withdrawing}
              >
                {withdrawing ? 'Processing...' : `Withdraw ${formatCurrency(displayBalance)}`}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PlanWithdrawPage;